import Title from '@components/title/Title'
import File from '@components/files/File'
import add from '@assets/form/plus.svg'
import { useAppDispatch, useAppSelector } from '@/hooks'
import type { RootState } from '@/store'
import { Person, Corporation } from '@/api/form/v1/form_pb'
import { setIdFile, setNonFinancingDeclarationFile } from './personSlice'
import { setCorporationKbisFile, setCorporationStatutsFile } from './corporationSlice'
import {
	setCorporationDelegateIdFile,
	setCorporationNonFinancingDeclarationFile,
} from './corporationSlice'
import { schemaValidator } from '@/utils/validators'
import type { FieldRules } from '../fieldRules'

import './Section.css'

interface FilesProps {
	buyerType: string
	fundingType: string
	// What the schema says about each field.
	rules: FieldRules
	// The answers the whole form shares, for the schema's conditions.
	answers: Record<string, string>
}

function Files(props: FilesProps) {
	const dispatch = useAppDispatch()
	const { rules } = props
	const answers = { ...props.answers, fundingType: props.fundingType }
	// Whether the schema shows this document to the buyer at this position.
	const asksFor = (key: string, position: number) =>
		rules.shows(key, answers) && (position === 0 || !rules.isFirstBuyerOnly(key))
	const documentValidator = (key: string) => schemaValidator(rules.requires(key))

	// **** getters ****
	const persons = useAppSelector((state: RootState) => state.person.persons)
	const corporations = useAppSelector((state: RootState) => state.corporation.corporations)

	const fullName = (person: Person) => {
		return `${person.firstName} ${person.lastName}`
	}

	const corpoDelegateFullName = (corpo: Corporation) => {
		return `${corpo.delegateFirstName} ${corpo.delegateLastName}`
	}

	const handleSetIdFile = (id: string, idFile: string) => {
		dispatch(setIdFile({ id: id, value: idFile }))
	}

	const handleSetKbis = (id: string, path: string) => {
		dispatch(setCorporationKbisFile({ id, value: path }))
	}

	const handleSetStatus = (id: string, path: string) => {
		dispatch(setCorporationStatutsFile({ id, value: path }))
	}

	const handleSetNonFinancingDeclaration = (id: string, path: string) => {
		if (props.buyerType === 'person') {
			dispatch(setNonFinancingDeclarationFile({ id: id, value: path }))
		} else {
			dispatch(setCorporationNonFinancingDeclarationFile({ id: id, value: path }))
		}
	}

	const handleSetCorpoId = (id: string, idFile: string) => {
		dispatch(setCorporationDelegateIdFile({ id: id, value: idFile }))
	}

	if (
		!rules.asks(
			'idFile',
			'nonFinancingDeclarationFile',
			'delegateIdFile',
			'corporationKbisFile',
			'corporationStatutsFile',
			'corporationNonFinancingDeclarationFile',
		)
	) {
		return null
	}

	return (
		<div className='section'>
			<Title>Vos documents</Title>
			{props.buyerType === 'person' &&
				Object.values(persons).map((person, position) => (
					<div key={person.id}>
							<div className='underSectionSeparator'>{fullName(person)}</div>
							<div className='filesContent'>
								{asksFor('idFile', position) && (
									<File
										id={`${person.id}-id`}
										label={'Pièce d’identité'}
										placeholder={add}
										value={person.idFile}
										validator={documentValidator('idFile')}
										onChange={path => handleSetIdFile(person.id, path)}
										style={{ cursor: 'pointer' }}
									/>
								)}
								{asksFor('nonFinancingDeclarationFile', position) && (
									<File
										id={`${person.id}-ficanancingDeclaration`}
										label={'Renonciation de financement'}
										placeholder={add}
										value={person.nonFinancingDeclarationFile}
										validator={documentValidator('nonFinancingDeclarationFile')}
										onChange={path => handleSetNonFinancingDeclaration(person.id, path)}
										style={{ cursor: 'pointer' }}
									/>
								)}
							</div>
						</div>
					))}
			{props.buyerType === 'corporation' &&
				Object.values(corporations).map((corporation, position) => (
					<div key={corporation.id}>
							<div className='underSectionSeparator'>{corpoDelegateFullName(corporation)}</div>
							<div className='filesContent'>
								{asksFor('delegateIdFile', position) && (
									<File
										key={`${corporation.id}-id`}
										id={`${corporation.id}-id`}
										label={'Pièce d’identité'}
										placeholder={add}
										value={corporation.delegateIdFile}
										validator={documentValidator('delegateIdFile')}
										onChange={path => handleSetCorpoId(corporation.id, path)}
										style={{ cursor: 'pointer' }}
									/>
								)}
								{asksFor('corporationKbisFile', position) && (
									<File
										key={`${corporation.id}-kbis`}
										id={`${corporation.id}-kbis`}
										label='Kbis'
										placeholder={add}
										value={corporation.kbisFile}
										validator={documentValidator('corporationKbisFile')}
										onChange={kbis => handleSetKbis(corporation.id, kbis)}
										style={{ cursor: 'pointer' }}
									/>
								)}
								{asksFor('corporationStatutsFile', position) && (
									<File
										key={`${corporation.id}-status`}
										id={`${corporation.id}-status`}
										label='Status'
										placeholder={add}
										value={corporation.statutsFile}
										validator={documentValidator('corporationStatutsFile')}
										onChange={status => handleSetStatus(corporation.id, status)}
										style={{ cursor: 'pointer' }}
									/>
								)}
								{asksFor('corporationNonFinancingDeclarationFile', position) && (
									<File
										key={`${corporation.id}-ficanancingDeclaration`}
										id={`${corporation.id}-ficanancingDeclaration`}
										label={'Renonciation de financement'}
										placeholder={add}
										value={corporation.nonFinancingDeclarationFile}
										validator={documentValidator('corporationNonFinancingDeclarationFile')}
										onChange={path => handleSetNonFinancingDeclaration(corporation.id, path)}
										style={{ cursor: 'pointer' }}
									/>
								)}
							</div>
						</div>
					))}
		</div>
	)
}

export default Files
